/**
 * Refused suggestions as sentences: suggesting a change to a record,
 * suggesting a new resource, withdrawing one, and approving or rejecting
 * one in the queue. The body's code picks the sentence; a 404 with any
 * other code reads that the suggestion is gone; anything else falls to
 * the house save sentence by status. The body's message, reason and
 * detail are never read.
 */

import { ApiError } from './api';
import { contactSentence, errorCodeOf, pageWriteText, type SupportContact } from './errorCopy';

const ALREADY_REVIEWED =
  'This suggestion has already been reviewed. Refresh the list to see where it stands.';

/** One sentence per refusal code the suggestion routes answer with.
 *  `{contact_admin}` is the admin contact. */
export const SUGGESTION_REFUSALS: Readonly<Record<string, string>> = {
  target_not_found: "This record can't be found. Reload the page.",
  vacuous_suggestion: "One of the new values is the same as what's there now. Change it, or remove that field.",
  invalid_field: "That field can't be suggested yet. Tell {contact_admin} what needs to change instead.",
  invalid_value:
    "One of your changes isn't in the right shape for its box. Check each box you changed for a full phone number, email address or website, then send again.",
  invalid_body: "That suggestion couldn't be sent. Check the required fields, then send it again.",
  category_slug_not_found:
    "That service category isn't available any more. Pick another, then send it again.",
  suggested_edit_not_pending: ALREADY_REVIEWED,
  suggested_resource_not_pending: ALREADY_REVIEWED,
  not_own_suggestion: 'Only the person who made this suggestion can withdraw it. Ask them, or reject it from the queue.',
};

/** A 404 whose code is not listed above. */
export const SUGGESTION_GONE = 'This suggestion is gone. Reload the list.';

/** A refused suggestion as one sentence: by code first, then a 404 as
 *  gone, then the house save sentence by status. */
export function suggestionRefusalText(e: unknown, contact: SupportContact): string {
  const code = errorCodeOf(e);
  const sentence = code === null ? undefined : SUGGESTION_REFUSALS[code];
  if (sentence !== undefined) return contactSentence(sentence, contact);
  if (e instanceof ApiError && e.status === 404) return SUGGESTION_GONE;
  return pageWriteText(e, contact);
}
